import React, { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Checkbox,
  Typography,
  message,
  Skeleton,
  Tag,
  Divider,
} from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDevice } from "../../../hooks/useDevice";
import {
  GET_MESSAGE_FEEDBACK_OPTIONS_ACTIVITY_CENTER,
  GET_MESSAGE_FEEDBACKS,
} from "../../../graphql/query";
import { CREATE_MESSAGE_FEEDBACK, UPDATE_MESSAGE_FEEDBACK } from "../../../graphql/mutation";
import { getDefaultOpportunity, prepareOpportunityList } from "../../../helpers/opportunityHelper";
import { compareReasonsOtherLast } from "../../../helpers/aiFeedbackHelper";
import { getVehicleName } from "./engagementHelpers";

const MessageFeedback = ({
  isDrawerOpen,
  onClose,
  onFeedbackSubmit,
  form,
  messageId,
  user,
  feedback,
  feedbackNote = "",
  feedbackOptionIds = [],
  messageContent = "",
  reasonForResponse = "",
  campaign = null,
  lead = null,
  // Which surface this rating came from — AUTORESPONDER (thread message),
  // CAMPAIGN (Otto message in the thread center) or DRAFT (composer).
  feedbackType = null,
  // DRAFT only: identifies the rated draft in place of a messageId.
  aiResponseHistoryId = null,
  // EAI-1762: rating an AI draft in the composer. There is no message row, so
  // the create is keyed by aiResponseHistoryId instead of messageId.
  isDraft = false,
  // Header above the message block, and whether to offer the "Why was this
  // message sent?" reason drawer (a draft has no send reason).
  messageContentLabel = "Message Sent",
  showReasonLink = true,
}) => {
  const { isMobile } = useDevice();
  // Controls the nested "Why was this message sent?" drawer (the Otto Response
  // reason behind why this autoresponder message was sent).
  const [isReasonOpen, setIsReasonOpen] = useState(false);

  // Lead details for the "Lead Info" section of the drawer.
  const crmIntegrationType = user?.company?.crmIntegrationType;
  const leadName = lead?.fullName;
  const leadLocation = lead?.location?.title || lead?.location?.name;
  const leadStatus = lead?.leadStatusType?.type;
  // The tag on the "Message Sent" block shows the campaign / message type
  // (e.g. "New Internet Leads", "Auto Responder", "Price Alert") — this mirrors
  // the orange campaign chip in MessageMetaInfo. It is NOT the lead source.
  const campaignName = campaign?.name && campaign.name !== "Default" ? campaign.name : null;
  const defaultOpportunity = getDefaultOpportunity(lead?.leadOpportunities);
  const [formattedOpportunity] = prepareOpportunityList(
    defaultOpportunity ? [defaultOpportunity] : [],
    crmIntegrationType
  );
  const opportunityLabel = formattedOpportunity
    ? `${formattedOpportunity.prefix} ${formattedOpportunity.eventId}`
    : null;
  const vehicleList = defaultOpportunity?.vehiclesOfInterest?.length
    ? defaultOpportunity.vehiclesOfInterest
    : lead?.vehicleOfInterest || [];
  const primaryVehicle = vehicleList.find(v => v?.isPrimary) || vehicleList[0];
  const vehicleName = getVehicleName(primaryVehicle);
  const hasLeadInfo = leadName || leadStatus || opportunityLabel || vehicleName;
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  // The Good/Negative mode is chosen from the thumb the user clicked on the
  // message itself and passed in via the `feedback` prop.
  const isNegative = feedback === "bad";

  const isAllLocation =
    sessionStorage.getItem("currentLocationId") &&
    sessionStorage.getItem("currentLocationId").includes(",");

  // Query to get feedback options
  const { loading: optionsLoading } = useQuery(GET_MESSAGE_FEEDBACK_OPTIONS_ACTIVITY_CENTER, {
    variables: {
      // Under an all-locations selection the options are normally scoped by
      // messageId. A draft has no message yet, so fall back to the lead's own
      // location to keep the reason list location-correct.
      locationId: isAllLocation
        ? (isDraft && lead?.location?.id) || null
        : sessionStorage.getItem("currentLocationId") || user?.locationId,
      companyId: user?.company?.id,
      messageId: isAllLocation ? messageId ?? null : null,
    },
    fetchPolicy: "network-only",
    skip: !isDrawerOpen,
    onCompleted: data => {
      if (data?.getMessageFeedbackOptions) {
        setFeedbackOptions(data.getMessageFeedbackOptions);
      }
    },
    onError: error => {
      console.error("Error fetching feedback options:", error);
      message.error("Failed to load feedback options");
    },
  });

  // Lazy query to get existing feedback for this message
  const [getExistingFeedback, { loading: existingFeedbackLoading }] = useLazyQuery(
    GET_MESSAGE_FEEDBACKS,
    {
      fetchPolicy: "network-only",
      skip: !isDrawerOpen || !messageId,
      variables: { messageId, userId: user?.id },
      onCompleted: data => {
        if (data?.getMessageFeedback) {
          const feedback = data.getMessageFeedback;

          setExistingFeedback(feedback);
          // Pre-populate form with existing feedback
          form?.setFieldsValue({
            feedbackOptionIds: (feedback?.messageFeedbackOptionSelection || []).map(
              option => option.messageFeedbackOption.id
            ),
            additionalComments: feedback.additionalComments,
          });
        }
      },
      onError: error => {
        console.error("Error fetching existing feedback:", error);
      },
    }
  );

  const handleDrawerClose = (flag = false) => {
    setExistingFeedback(null);
    form?.resetFields();
    onClose(flag);
  };

  // Mutation to create new feedback
  const [createFeedback, { loading: createLoading }] = useMutation(CREATE_MESSAGE_FEEDBACK, {
    onCompleted: data => {
      if (data?.createMessageFeedback) {
        message.success("Feedback submitted successfully!");

        handleDrawerClose();
      }
    },
    onError: error => {
      console.error("Error creating feedback:", error);
      message.error("Failed to submit feedback. Please try again.");
    },
  });

  // Mutation to update existing feedback
  const [updateFeedback, { loading: updateLoading }] = useMutation(UPDATE_MESSAGE_FEEDBACK, {
    onCompleted: data => {
      if (data?.updateMessageFeedback) {
        message.success("Feedback updated successfully!");
      }
    },
    onError: error => {
      console.error("Error updating feedback:", error);
      message.error("Failed to update feedback. Please try again.");
    },
  });

  useEffect(() => {
    if (!isDrawerOpen) {
      setExistingFeedback(null);
    }
  }, [isDrawerOpen]);

  // feedbackNote is not used in this component but can be passed to form if needed
  useEffect(() => {
    if (isDrawerOpen && feedbackNote && !existingFeedback) {
      form?.setFieldsValue({
        additionalComments: feedbackNote,
      });
    }
    if (isDrawerOpen && feedbackOptionIds && feedbackOptionIds.length > 0 && !existingFeedback) {
      form?.setFieldsValue({
        feedbackOptionIds: feedbackOptionIds,
      });
    }
  }, [isDrawerOpen, feedbackNote, feedbackOptionIds, form, existingFeedback]);

  // Effect to fetch existing feedback (current user) when drawer opens.
  // If the user already has feedback on this message we update it; otherwise create.
  useEffect(() => {
    if (isDrawerOpen && messageId) {
      getExistingFeedback({ variables: { messageId, userId: user?.id } });
    }
  }, [isDrawerOpen, messageId, getExistingFeedback]);

  const handleFeedbackSubmit = async values => {
    setLoading(true);

    try {
      if (existingFeedback) {
        // Update existing feedback. Rating comes from the thumb the user
        // clicked, so an edit can still flip Good <-> Negative in place.
        const nextRating = feedback.toUpperCase(); // "GOOD" | "BAD"
        // Reason options only apply to Negative. On Good the backend clears any
        // stale selections, so we send an empty list to keep client/server in sync.
        const nextOptionIds = nextRating === "BAD" ? values.feedbackOptionIds || [] : [];
        // Send the field value (trimmed) so clearing the optional comment
        // actually persists. `|| existingFeedback...` used to resurrect the old
        // comment whenever the field was emptied; `.trim()` also treats a
        // whitespace-only entry as cleared. Empty string is intentionally sent
        // (the field is a nullable String, and the backend clears on "").
        const nextComment = (values.additionalComments ?? "").trim();
        await updateFeedback({
          variables: {
            messageFeedbackId: existingFeedback.id,
            rating: nextRating,
            feedbackOptionIds: nextOptionIds,
            additionalComments: nextComment,
          },
        }).then(resp => {
          if (resp?.data?.updateMessageFeedback?.statusCode === 200) {
            onFeedbackSubmit?.({
              messageId: messageId,
              // Store uppercase ("GOOD"/"BAD") so the thumb state survives a
              // re-render: messages.js seeds mode from messageFeedback.includes("BAD").
              feedback: nextRating,
              feedbackNote: nextComment,
              feedbackOptionIds: nextOptionIds,
            });
            // handleDrawerClose();
          }
        });
      } else {
        const feedbackData = {
          messageId,
          // messageId is optional on the mutation now; a rating with no message
          // row (draft) is keyed by the AI response instead.
          aiResponseHistoryId: messageId ? null : aiResponseHistoryId,
          feedbackType,
          feedbackOptionIds: values.feedbackOptionIds,
          // Trim first so a whitespace-only entry is stored as null, not spaces.
          additionalComments: (values.additionalComments || "").trim() || null,
          rating: feedback.toUpperCase(), // "GOOD" or "BAD"
        };
        // Create new feedback
        await createFeedback({
          variables: {
            ...feedbackData,
          },
        }).then(resp => {
          if (resp?.data?.createMessageFeedback?.statusCode === 200) {
            onFeedbackSubmit?.({
              messageId: feedbackData.messageId,
              feedback: feedbackData.rating,
              feedbackNote: feedbackData.additionalComments,
              feedbackOptionIds: feedbackData.feedbackOptionIds || [],
            });
          }
        });
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if form is loading
  const isFormLoading = loading || createLoading || updateLoading || existingFeedbackLoading;

  // Shared Lead Info card — rendered both in the feedback form and in the
  // "Why was this message sent?" reason drawer so the two stay identical.
  const leadInfoSection = hasLeadInfo ? (
    <div className="mb-6">
      <Typography.Text className="block mb-2 text-xs font-semibold uppercase text-gray-400">
        Lead Info
      </Typography.Text>
      <div className="rounded-md border border-solid border-gray-200 p-3">
        <div className="flex items-start justify-between">
          <div>
            {leadName && <div className="font-semibold text-gray-900">{leadName}</div>}
            {leadStatus && <div className="text-xs text-gray-500 mt-0.5">{leadStatus}</div>}
          </div>
          {leadLocation && (
            <span className="text-xs text-gray-500 whitespace-nowrap pl-2">{leadLocation}</span>
          )}
        </div>
        {(opportunityLabel || vehicleName) && <Divider className="my-2" />}
        {opportunityLabel && (
          <div className="flex text-sm mb-1">
            <span className="w-24 shrink-0 text-gray-500">Opportunity:</span>
            <span className="text-[#1677FF]">{opportunityLabel}</span>
          </div>
        )}
        {vehicleName && (
          <div className="flex text-sm">
            <span className="w-24 shrink-0 text-gray-500">Vehicle:</span>
            <span className="text-gray-800">{vehicleName}</span>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <Drawer
        destroyOnClose
        title={existingFeedback ? "Edit Message Feedback" : "Add Message Feedback"}
        placement="right"
        width={isMobile ? "100%" : 600}
        onClose={() => handleDrawerClose(true)}
        open={isDrawerOpen}
        footer={
          <div className="flex justify-start gap-2">
            <Button type="primary" onClick={() => form?.submit()} loading={isFormLoading}>
              {existingFeedback ? "Update Feedback" : "Submit Feedback"}
            </Button>
            <Button onClick={() => handleDrawerClose(true)} disabled={isFormLoading}>
              Cancel
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleFeedbackSubmit}>
          {leadInfoSection}
          {messageContent && (
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between">
                <Typography.Text className="text-xs font-semibold uppercase text-gray-400">
                  {messageContentLabel}
                </Typography.Text>
                {campaignName && <Tag color="blue">{campaignName}</Tag>}
              </div>
              <div
                className="rounded-md bg-gray-50 p-3"
                style={{ borderLeft: "4px solid #1677FF" }}
              >
                <Typography.Paragraph className="mb-0 text-gray-700 whitespace-pre-wrap">
                  {messageContent}
                </Typography.Paragraph>
              </div>
              {showReasonLink && (
                <button
                  type="button"
                  onClick={() => setIsReasonOpen(true)}
                  className="mt-2 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm text-[#1677FF] hover:underline"
                >
                  <InfoCircleFilled />
                  Why was this message sent?
                </button>
              )}
            </div>
          )}
          {isNegative && (feedbackOptions.length > 0 || optionsLoading) && (
            <div className="mb-6">
              <Typography.Title level={5} className="mb-2">
                Why was this unhelpful?
              </Typography.Title>
              <Form.Item
                name="feedbackOptionIds"
                rules={[
                  {
                    // Required only when submitting brand-new Negative feedback.
                    // On edit the reason selection is optional (business rule).
                    required: !existingFeedback,
                    message: "Please select at least one reason",
                  },
                ]}
              >
                <Checkbox.Group
                  className="flex flex-col space-y-2"
                  disabled={!existingFeedback && feedbackOptionIds.length > 0}
                >
                  {optionsLoading ? (
                    <div className="text-center py-4">
                      <Skeleton active paragraph={{ rows: 4 }} />
                    </div>
                  ) : feedbackOptions.length > 0 ? (
                    feedbackOptions
                      .filter(option => option.isActive)
                      // Alphabetical, but always keep "Other" pinned to the bottom.
                      .sort(compareReasonsOtherLast)
                      .map(option => (
                        <Checkbox key={option.id} value={option.id}>
                          {option.name}
                        </Checkbox>
                      ))
                  ) : null}
                </Checkbox.Group>
              </Form.Item>
            </div>
          )}
          <Form.Item
            label={
              <span>
                Additional comments <span className="text-gray-400">(optional)</span>
              </span>
            }
            name="additionalComments"
          >
            <Input.TextArea
              rows={4}
              placeholder="Write your feedback here..."
              disabled={isFormLoading}
            />
          </Form.Item>
        </Form>
      </Drawer>
      <Drawer
        title="Why was this message sent?"
        placement="right"
        width={isMobile ? "100%" : 600}
        open={isReasonOpen}
        onClose={() => setIsReasonOpen(false)}
        footer={
          <div className="flex justify-start">
            <Button onClick={() => setIsReasonOpen(false)}>Close</Button>
          </div>
        }
      >
        {leadInfoSection}
        <div className="mb-6">
          <Typography.Text className="block mb-2 text-xs font-semibold uppercase text-gray-400">
            Reason
          </Typography.Text>
          <div className="rounded-md bg-gray-50 p-3">
            {reasonForResponse ? (
              <Typography.Paragraph className="mb-0 text-gray-700 whitespace-pre-wrap">
                {reasonForResponse}
              </Typography.Paragraph>
            ) : (
              <span className="italic text-gray-400">No reason is available for this message.</span>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default MessageFeedback;
